import { useQuery } from "@tanstack/react-query";
import { fetchImportStatus } from "../api/imports";
import { useAuth } from "./useAuth";

export const IMPORT_STATUS_KEY = ["imports", "status"] as const;

/** Poll interval while an import is active. Idle = no polling at all. */
export const IMPORT_POLL_MS = 10_000;

/**
 * GET /imports/status, polled only while an import is in flight (and while
 * forced, e.g. just after "Sync now" before the worker has made its run row). Refetches when the tab regains focus; TanStack skips the
 * interval while the tab is hidden.
 */
export function useImportStatus({
  forcePoll = false,
  forcePollUntil = 0,
}: {
  /** Poll while true (e.g. the integrations list shows a run in flight). */
  forcePoll?: boolean;
  /**
   * Poll until this epoch ms. Compared inside the interval callback, so the
   * poll switches itself off when the window ends even if nothing re-renders.
   */
  forcePollUntil?: number;
} = {}) {
  const { user } = useAuth();
  return useQuery({
    queryKey: IMPORT_STATUS_KEY,
    queryFn: fetchImportStatus,
    enabled: !!user,
    retry: false,
    // Short so a focus or remount picks up a sync started elsewhere.
    staleTime: 5_000,
    refetchOnWindowFocus: true,
    refetchInterval: (query) =>
      query.state.data?.active || forcePoll || Date.now() < forcePollUntil
        ? IMPORT_POLL_MS
        : false,
  });
}
